import { useTranslation } from "react-i18next";
import { DeleteOutlined, DownOutlined, ExclamationCircleFilled, FilterOutlined, HeartOutlined } from "@ant-design/icons";
import { Button, Col, DatePicker, Form, message, Modal, Radio, Row, Spin, Typography } from "antd";
import { viLocale as locale } from "../../../../utils/pickerType";
import { RadioChangeEvent } from "antd/lib";
import dayjs from "dayjs";
import { isEmpty, toNumber } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import FormInput from "../../../../components/form-input/FormInput";
import useKeyword from "../../../../hooks/useKeyword";
import { discussionReact, getCourse } from "../../../../service/course-construct/statistic";
import { countDiscussion, deleteTeacherReplyStudent, getAllDiscussionUnit, removeDiscussion, saveDiscussion, seenComment, teacherReplyStudent, unSaveDiscussion, updateTeacherReplyStudent } from "../../../../service/course-management";
import { CourseManagementStore } from "../../../../stores/stores";
import { CourseDetail } from "../../../../types/course";
import { DiscussionProps } from "../../../../types/course-construction";
import "./DiscussionCourse.scss";
import DiscussionReport from "./components/DiscussionReport";
import DiscussionSaved from "./components/DiscussionSaved";
import MenuSlider from "./components/MenuSlider";
import Discuss from "./components/discuss/Discuss";
import CommentFeed, { Discussion } from "./components/discuss/comments/CommentFeed";
import { MenuOption } from "./components/discuss/comments/CommentSection";
import { DISLIKE_REACTION, LIKE_REACTION } from "../../../../constants";
import { CommentNode } from "./components/discuss/comments/commentTree";
import { RawComment } from "./components/discuss/comments/commentTypes";

interface TotalDiscussions {
  seen: number;
  notSeen: number;
  report: number;
  save: number;
}

function DiscussionCourse() {
  const { t } = useTranslation();
  const { courseId } = useParams();
  const { reloadDiscussion, setDiscussionMode } = CourseManagementStore();
  const [form] = Form.useForm();
  const unitId = toNumber(useKeyword("unitId"));
  const { Title } = Typography;
  const { RangePicker } = DatePicker;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [mode, setMode] = useState<string>('1');
  const [course, setCourse] = useState<CourseDetail>();
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [filterData, setFilterData] = useState({ student: "", timeCreated: [] });
  const [comments, setComments] = useState<{
    total: number;
    discussions: DiscussionProps[];
  }>();
  const [totalDiscussions, setTotalDiscussions] = useState<TotalDiscussions>({
    seen: 0,
    notSeen: 0,
    report: 0,
    save: 0
  });
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [reloadTotal, setReloadTotal] = useState<boolean>(false)

  useEffect(() => {
    const getCourses = async () => {
      if (courseId) {
        try {
          const res = await getCourse(Number(courseId));
          setCourse(res.data.data);
        } catch (err) {
          console.error(err);
        }
      }
    };
    getCourses();
  }, [courseId]);

  const handleModeChange = (e: RadioChangeEvent) => {
    setMode(e.target.value);
    setReloadTotal(!reloadTotal)
    setDiscussionMode(e.target.value);
  };

  const handleOpenFilter = () => {
    setShowFilter(!showFilter);
  };

  const handleFinish = (values: any) => {
    setFilterData(values);
  };

  const handleReset = () => {
    form.resetFields();
    setFilterData({ student: "", timeCreated: [] });
  };

  const handleDataUpdate = useCallback((updatedData: Discussion[]) => {
    setDiscussions(updatedData);
  }, []);

  const handleEdit = async (id: number, content: string, attachments?: any) => {
    try {
      const formData = new FormData();
      formData.append("id", id.toString());
      formData.append('content', content);

      if (attachments?.newAttachments) {
        attachments.newAttachments.forEach((file: File) => {
          formData.append('attachments', file);
        });
      }

      if (attachments?.removedAttachmentIds) {
        formData.append('deleteAttachmentIds', attachments.removedAttachmentIds?.join(","));
      }

      const response = await updateTeacherReplyStudent(formData);
      return response.data.data;
    } catch (error) {
      message.error(t("Không thể cập nhật", "Không thể cập nhật"));
      throw error;
    }
  };

  const handleDelete = async (node: CommentNode, isAuthorDelete: boolean = false) => {
    const isDiscussion = node.level === 1;
    const entityType = isDiscussion ? 'thảo luận' : 'phản hồi';
    const actionType = isAuthorDelete ? 'xóa' : 'gỡ';
    const modalTitle = isAuthorDelete
      ? `Xác nhận xóa ${entityType}`
      : `Xác nhận gỡ ${entityType}`;
    const modalContent = isAuthorDelete
      ? `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} của bạn sẽ bị xóa vĩnh viễn và không thể khôi phục. Bạn có chắc chắn?`
      : `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} của ${node.author?.fullName} sẽ bị gỡ. Bạn có chắc chắn?`;

    return new Promise<void>((resolve, reject) => {
      Modal.confirm({
        title: modalTitle,
        icon: isAuthorDelete ? <ExclamationCircleFilled /> : <ExclamationCircleFilled style={{ color: "#FFCC00" }} />,
        content: modalContent,
        okText: `${actionType.charAt(0).toUpperCase() + actionType.slice(1)} ${entityType}`,
        okType: isAuthorDelete ? "danger" : "default",
        cancelText: t("Huỷ", "Huỷ"),
        centered: true,
        onOk: async () => {
          try {
            if (isDiscussion) {
              await removeDiscussion(node.discussionId || node.id);
              setDiscussions(prev => prev.filter(d => d.id !== (node.discussionId || node.id)));

              setTotalDiscussions(prev => ({
                ...prev,
                [mode === '0' ? 'notSeen' : 'seen']: Math.max(0, prev[mode === '0' ? 'notSeen' : 'seen'] - 1)
              }));
            } else {
              if (isAuthorDelete) {
                await deleteTeacherReplyStudent(node.id);
              } else {
                await removeDiscussion(node.id);
              }

              setDiscussions(prev => {
                return prev.map(discussion => {
                  const commentToDelete = discussion.subComments.find(c => c.id === node.id);
                  if (!commentToDelete) return discussion;

                  const filteredSubComments = discussion.subComments.filter(c => c.id !== node.id);

                  let updatedRootComment = discussion.rootComment;
                  let updatedSubComments = filteredSubComments;

                  if (commentToDelete.parentId) {
                    if (commentToDelete.parentId === discussion.rootComment.id) {
                      updatedRootComment = {
                        ...discussion.rootComment,
                        totalReply: Math.max(0, (discussion.rootComment.totalReply || 0) - 1)
                      };
                    } else {
                      updatedSubComments = filteredSubComments.map(comment => {
                        if (comment.id === commentToDelete.parentId) {
                          return {
                            ...comment,
                            totalReply: Math.max(0, (comment.totalReply || 0) - 1)
                          };
                        }
                        return comment;
                      });
                    }
                  }

                  return {
                    ...discussion,
                    rootComment: updatedRootComment,
                    subComments: updatedSubComments
                  };
                });
              });
            }

            message.success(`Đã ${actionType} ${entityType} thành công`);
            resolve();
          } catch (err) {
            message.error(`Không thể ${actionType} ${entityType}`);
            reject(err);
          }
        },
        onCancel: () => {
          reject(new Error('User cancelled'));
        },
      });
    });
  };

  const handleSubmitReply = async (data: {
    discussionId: number;
    parentId: number;
    replyToId: number;
    level: number;
    content: string;
    attachments?: File[];
  }): Promise<RawComment> => {
    const payload = new FormData();
    payload.append("content", data.content);
    payload.append("parentId", data.parentId.toString());

    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach((file) => {
        payload.append("attachments", file);
      });
    }

    try {
      const response = await teacherReplyStudent(payload);
      setReloadTotal(!reloadTotal)

      return response.data.data;
    } catch (error) {
      throw error;
    }
  };

  const handleReact = async (id: number, reactionType: 'like' | 'dislike') => {
    try {
      const type = reactionType === 'like' ? LIKE_REACTION : DISLIKE_REACTION;
      await discussionReact({ id, reactionType: type });
    } catch (error) {
      message.error(t("Không thể thực hiện", "Không thể thực hiện"));
      throw error;
    }
  };


  const handleSeenDiscuss = async (id: number) => {
    try {

      await seenComment(id);
      setReloadTotal(!reloadTotal)
    } catch (error) {
      message.error(t("Không thể thực hiện", "Không thể thực hiện"));
      throw error;
    }
  }

  const handleSaveDiscussion = async (discussionId: number, isSaved: boolean) => {
    try {
      const response = !isSaved ? await saveDiscussion(discussionId) : await unSaveDiscussion(discussionId);

      message.success(isSaved ? 'Đã bỏ quan tâm' : 'Đã quan tâm thảo luận');

      setDiscussions(prevDiscussions =>
        prevDiscussions.map(d =>
          d.id === discussionId
            ? { ...d, isSave: !isSaved }
            : d
        )
      );
    } catch (error) {
      message.error(t("Không thể thực hiện thao tác", "Không thể thực hiện thao tác"));
      throw error;
    }
  };

  const getMenuOptions = useCallback((comment: CommentNode): MenuOption[] => {
    const options: MenuOption[] = [];

    const discussion = discussions.find(d =>
      d.rootComment.id === comment.id ||
      d.subComments.some((c: any) => c.id === comment.id)
    );

    const isSaved = discussion?.isSave;
    const discussionId = discussion?.id || 0;
    const isAuthor = comment.isMine;

    if (comment.level === 1) {
      options.push({
        key: 'save',
        label: isSaved ? 'Bỏ quan tâm' : 'Quan tâm',
        icon: <HeartOutlined />,
        onClick: () => handleSaveDiscussion(discussionId, isSaved || false)
      });

      options.push({
        key: isAuthor ? 'delete' : 'remove',
        label: isAuthor ? "Xóa thảo luận" : "Gỡ thảo luận",
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => handleDelete(comment, isAuthor)
      });
    } else {
      options.push({
        key: isAuthor ? 'delete' : 'remove',
        label: isAuthor ? "Xóa phản hồi" : "Gỡ phản hồi",
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => handleDelete(comment, isAuthor)
      });
    }

    return options;
  }, [discussions]);

  const renderDiscussion = (tab: string) => {
    switch (tab) {
      case "0":
      case "1":
        return (
          <Spin spinning={isLoading}>
            {/* <Discuss comments={comments} /> */}
            <CommentFeed
              data={discussions || []}
              onEdit={handleEdit}
              onDelete={(node) => handleDelete(node, node.isMine)}
              onSubmitReply={handleSubmitReply}
              onReact={handleReact}
              onDataUpdate={handleDataUpdate}
              getMenuOptions={getMenuOptions}
              onSeenDiscuss={handleSeenDiscuss}
            />
          </Spin>
        );
      case "3":
        return <DiscussionReport mode={mode} filterData={filterData} />;
      case "4":
        return <DiscussionSaved filterData={filterData} />;
      default:
        return null;
    }
  };

  useEffect(() => {
    const getComments = async () => {
      setIsLoading(true);
      try {
        if (unitId) {
          const res = await getAllDiscussionUnit({
            unitId: unitId,
            status: mode,
            keyword: filterData.student,
            createdDateFrom: !isEmpty(filterData?.timeCreated)
              ? dayjs(filterData?.timeCreated?.[0]).toISOString()
              : undefined,
            createdDateTo: !isEmpty(filterData?.timeCreated)
              ? dayjs(filterData?.timeCreated?.[1]).toISOString()
              : undefined,
          });
          setComments(res.data.data);
          setDiscussions(res?.data?.data?.discussions);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (mode === "0" || mode === "1") {
      getComments();
    }
  }, [unitId, mode, filterData, reloadDiscussion]);

  useEffect(() => {
    const fetchCountDiscussion = async () => {
      try {
        if (unitId) {
          const res = await countDiscussion(unitId);
          setTotalDiscussions(res.data.data);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchCountDiscussion();
  }, [unitId, filterData, reloadDiscussion, reloadTotal]);

  return (
    <div className="discussion-course flex gap-3">
      <MenuSlider course={course} />
      <div className="discussion-course-content flex-1">
        <div className="flex justify-between">
          <Radio.Group onChange={handleModeChange} value={mode} style={{ marginBottom: 8 }}>
            <Radio.Button value="1">{t("Đã xem", "Đã xem")} {`(${totalDiscussions?.seen})`}</Radio.Button>
            <Radio.Button value="0">{t("Chưa xem", "Chưa xem")} {`(${totalDiscussions?.notSeen})`}</Radio.Button>
            <Radio.Button value="3">{t("Báo cáo", "Báo cáo")} {`(${totalDiscussions?.report})`}</Radio.Button>
            <Radio.Button value="4">{t("Quan tâm", "Quan tâm")} {`(${totalDiscussions?.save})`}</Radio.Button>
          </Radio.Group>
          <div className="heading-filter">
            <Button className="filter-button" onClick={handleOpenFilter}>
              <Typography.Text>{t("Bộ lọc", "Bộ lọc")}</Typography.Text>
              <DownOutlined />
            </Button>
          </div>
        </div>

        {showFilter && (
          <Form form={form} onFinish={handleFinish} layout="vertical">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Title level={5}>{t("Học viên", "Học viên")}</Title>
                <Form.Item name="student" className="mb-0">
                  <FormInput placeholder={t("Nhập tên học viên", "Nhập tên học viên")} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Title level={5}>{t("Thời gian tạo", "Thời gian tạo")}</Title>
                <Form.Item name="timeCreated" className="mb-0">
                  <RangePicker
                    format={"YYYY-MM-DD HH:mm"}
                    locale={locale}
                    showTime
                    placeholder={["Bắt đầu", "Kết thúc"]}
                  />
                </Form.Item>
              </Col>
              <Col xl={24} md={24} sm={24} xs={24}>
                <div className="group-btn--filter">
                  <Button className="btn btn-primary search-btn" onClick={() => form.submit()}>
                    <FilterOutlined />
                    <Typography.Text>{t("Lọc", "Lọc")}</Typography.Text>
                  </Button>
                  <Button className="btn btn-outlined clear-filter-btn" onClick={handleReset}>
                    <DeleteOutlined />
                    <Typography.Text>{t("Xóa bộ lọc", "Xóa bộ lọc")}</Typography.Text>
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        )}
        {renderDiscussion(mode)}
      </div>
    </div>
  );
}

export default DiscussionCourse;